import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Search, RefreshCw, Snowflake } from "lucide-react";
import { toast } from "sonner";

const fmtRp = (n) => `Rp ${Number(n || 0).toLocaleString("id-ID")}`;
const emptyUnit = () => ({
  merk: "", tipe: "", kapasitas_pk: 1.0, harga: 0,
  keterangan: "", active: true,
});

export default function PriceListUnitsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [merkFilter, setMerkFilter] = useState("__all__");
  const [dialog, setDialog] = useState(null);   // {mode:'add'|'edit', item}

  async function load() {
    setLoading(true);
    try {
      const r = await api.listPriceListUnits();
      setItems(r.items || []);
    } catch (e) {
      toast.error("Gagal muat: " + (e?.response?.data?.detail || String(e)));
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  const merks = useMemo(
    () => Array.from(new Set(items.map(i => i.merk))).sort(),
    [items]
  );

  const filtered = useMemo(() => items.filter(i => {
    if (merkFilter !== "__all__" && i.merk !== merkFilter) return false;
    if (!q) return true;
    const s = q.toLowerCase();
    return (i.merk || "").toLowerCase().includes(s)
        || (i.tipe || "").toLowerCase().includes(s)
        || (i.keterangan || "").toLowerCase().includes(s);
  }), [items, q, merkFilter]);

  async function save() {
    const it = dialog.item;
    if (!it.merk.trim()) { toast.error("Merk wajib"); return; }
    if (!it.tipe.trim()) { toast.error("Tipe wajib"); return; }
    const pk = Number(it.kapasitas_pk);
    if (!pk || pk <= 0) { toast.error("Kapasitas PK harus > 0"); return; }
    if (it.harga < 0) { toast.error("Harga tidak valid"); return; }
    try {
      const payload = {
        merk: it.merk.trim(),
        tipe: it.tipe.trim(),
        kapasitas_pk: pk,
        harga: Math.round(Number(it.harga)),
        keterangan: (it.keterangan || "").trim(),
        active: it.active !== false,
      };
      if (dialog.mode === "add") {
        await api.createPriceListUnit(payload);
        toast.success(`Unit "${it.merk} ${it.tipe}" ditambahkan`);
      } else {
        await api.updatePriceListUnit(it.id, payload);
        toast.success("Perubahan disimpan");
      }
      setDialog(null);
      load();
    } catch (e) {
      toast.error("Gagal simpan: " + (e?.response?.data?.detail || String(e)));
    }
  }

  async function del(item) {
    if (!window.confirm(`Hapus unit "${item.merk} ${item.tipe} ${item.kapasitas_pk} PK"?`)) return;
    try {
      await api.deletePriceListUnit(item.id);
      toast.success("Unit dihapus");
      load();
    } catch (e) {
      toast.error("Gagal hapus: " + (e?.response?.data?.detail || String(e)));
    }
  }

  return (
    <div className="space-y-4" data-testid="price-list-tab">
      {/* Header */}
      <div className="bg-white border border-border rounded-sm p-4 flex flex-col md:flex-row md:items-center gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
            <Snowflake className="w-3 h-3 text-sky-500" />
            <span className="font-semibold">Daftar Harga Unit AC</span>
          </div>
          <div className="text-[13px] text-zinc-600">
            <b className="font-mono text-zinc-800">{items.length}</b> unit ·
            AI WhatsApp bot & Admin AI otomatis pakai data ini saat customer tanya harga unit AC.
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={load} className="rounded-sm gap-1.5 h-9"
            data-testid="price-list-refresh-btn">
            <RefreshCw className="w-4 h-4" />Refresh
          </Button>
          <Button onClick={() => setDialog({ mode: "add", item: emptyUnit() })}
            className="bg-sky-600 hover:bg-sky-700 rounded-sm gap-1.5 h-9"
            data-testid="price-list-add-btn">
            <Plus className="w-4 h-4" />Tambah Unit
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-400" />
          <Input placeholder="Cari merk / tipe / keterangan…" value={q}
            onChange={e => setQ(e.target.value)}
            className="rounded-sm pl-8 h-9"
            data-testid="price-list-search-input" />
        </div>
        <select
          value={merkFilter}
          onChange={e => setMerkFilter(e.target.value)}
          className="rounded-sm border border-border h-9 px-3 text-[12px] bg-white"
          data-testid="price-list-merk-filter"
        >
          <option value="__all__">Semua Merk ({items.length})</option>
          {merks.map(m => (
            <option key={m} value={m}>{m} ({items.filter(i => i.merk === m).length})</option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="bg-white border border-border rounded-sm overflow-hidden"
        data-testid="price-list-table">
        {loading ? (
          <div className="p-8 text-center text-[12px] text-zinc-400">Memuat…</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-[12px] text-zinc-400"
            data-testid="price-list-empty">
            Belum ada unit AC. Klik <b>Tambah Unit</b> di atas.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]">
              <thead className="bg-zinc-50 text-zinc-500 text-[10px] uppercase tracking-widest">
                <tr>
                  <th className="text-left px-4 py-2 font-semibold">Merk</th>
                  <th className="text-left px-4 py-2 font-semibold">Tipe</th>
                  <th className="text-right px-4 py-2 font-semibold">Kapasitas (PK)</th>
                  <th className="text-right px-4 py-2 font-semibold">Harga (Rp)</th>
                  <th className="text-left px-4 py-2 font-semibold">Keterangan</th>
                  <th className="text-right px-4 py-2 font-semibold w-24">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(it => (
                  <tr key={it.id}
                    className={`border-t border-border hover:bg-zinc-50 ${it.active === false ? "opacity-50" : ""}`}
                    data-testid={`price-list-row-${it.id}`}>
                    <td className="px-4 py-2.5 font-semibold text-zinc-800">{it.merk}</td>
                    <td className="px-4 py-2.5 text-zinc-700 font-mono text-[11px]">{it.tipe}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-zinc-700">{it.kapasitas_pk}</td>
                    <td className="px-4 py-2.5 text-right font-mono font-bold text-sky-700"
                      data-testid={`price-list-harga-${it.id}`}>{fmtRp(it.harga)}</td>
                    <td className="px-4 py-2.5 text-[11px] text-zinc-500 italic">
                      {it.keterangan || <span className="text-zinc-300">—</span>}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <Button size="icon" variant="ghost"
                        onClick={() => setDialog({ mode: "edit", item: { ...it } })}
                        className="h-7 w-7 rounded-sm text-zinc-500 hover:text-sky-700"
                        data-testid={`price-list-edit-${it.id}`}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => del(it)}
                        className="h-7 w-7 rounded-sm text-zinc-500 hover:text-red-600"
                        data-testid={`price-list-delete-${it.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/Edit dialog */}
      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="rounded-sm max-w-lg" data-testid="price-list-dialog">
          <DialogHeader>
            <DialogTitle>
              {dialog?.mode === "add" ? "Tambah Unit AC Baru" : "Edit Unit AC"}
            </DialogTitle>
          </DialogHeader>
          {dialog && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Merk</label>
                  <Input value={dialog.item.merk}
                    onChange={e => setDialog({ ...dialog, item: { ...dialog.item, merk: e.target.value } })}
                    placeholder="mis. Daikin"
                    className="rounded-sm mt-1 h-9"
                    data-testid="price-list-form-merk" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Kapasitas (PK)</label>
                  <Input type="number" min="0" step="0.25" value={dialog.item.kapasitas_pk}
                    onChange={e => setDialog({ ...dialog, item: { ...dialog.item, kapasitas_pk: e.target.value } })}
                    className="rounded-sm mt-1 h-9 font-mono"
                    data-testid="price-list-form-pk" />
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Tipe</label>
                <Input value={dialog.item.tipe}
                  onChange={e => setDialog({ ...dialog, item: { ...dialog.item, tipe: e.target.value } })}
                  placeholder="mis. FTKQ25-BV / CS-YN9WKJ"
                  className="rounded-sm mt-1 h-9 font-mono"
                  data-testid="price-list-form-tipe" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Harga (Rp)</label>
                <Input type="number" min="0" step="10000" value={dialog.item.harga}
                  onChange={e => setDialog({ ...dialog, item: { ...dialog.item, harga: Number(e.target.value) || 0 } })}
                  className="rounded-sm mt-1 h-9 font-mono"
                  data-testid="price-list-form-harga" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Keterangan</label>
                <textarea value={dialog.item.keterangan || ""}
                  onChange={e => setDialog({ ...dialog, item: { ...dialog.item, keterangan: e.target.value } })}
                  placeholder="mis. R32, hemat listrik, low-watt, inverter"
                  rows={2}
                  className="w-full border border-border rounded-sm p-2 mt-1 text-[12px]"
                  data-testid="price-list-form-keterangan" />
              </div>
              <label className="flex items-center gap-2 text-[12px] text-zinc-700 cursor-pointer">
                <input type="checkbox" checked={dialog.item.active !== false}
                  onChange={e => setDialog({ ...dialog, item: { ...dialog.item, active: e.target.checked } })}
                  data-testid="price-list-form-active" />
                Aktif (tampilkan ke AI bot)
              </label>
              <div className="flex gap-2 pt-2">
                <Button onClick={save}
                  className="flex-1 bg-sky-600 hover:bg-sky-700 rounded-sm"
                  data-testid="price-list-form-save">Simpan</Button>
                <Button variant="outline" onClick={() => setDialog(null)} className="rounded-sm">Batal</Button>
              </div>
              <div className="text-[9px] text-zinc-400 pt-1 border-t border-border">
                💡 Data ini otomatis dibaca AI WhatsApp bot & Admin AI saat customer tanya harga unit AC.
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
